// This is the parent component of a modal: ModalRewardsone

import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import slugify from "slugify";
import { DealCard, DealCardMore } from "./prtnr";
import {
  fetchFavouriteVendorsItems,
  removeFavouriteVendor,
} from "./redux/slices/FavouriteVendorsThunks";
import { fetchFeaturedvendorsItems } from "./redux/slices/FeaturedvendorsThunks";
import { scrollToTop } from "./utils/func";

const generateSlugMap = (vendors) => {
  const slugCount = {};
  const slugMap = {};

  vendors.forEach((vendor) => {
    // Handle nested vendor_id structure
    const vendorData = vendor.vendor_id || vendor;
    const companyName = vendorData?.company_name;

    if (!companyName) return; // Skip if no company name

    const baseSlug = slugify(companyName, {
      lower: true,
      strict: true,
    });
    const count = slugCount[baseSlug] || 0;

    const finalSlug = count === 0 ? baseSlug : `${baseSlug}-${count}`;
    slugCount[baseSlug] = count + 1;

    // Map to the actual vendor ID
    slugMap[finalSlug] = vendorData._id;
  });

  return slugMap;
};

export default function Favourites() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const favouriteVendors = useSelector((state) => state.favouriteVendors.items);
  const featuredVendors = useSelector((state) => state.featuredvendors.items);

  const [featuredFavouriteVendors, setFeaturedFavouriteVendors] = useState([]);
  const [nonFeaturedFavouriteVendors, setNonFeaturedFavouriteVendors] =
    useState([]);

  const slugMap = useMemo(
    () => generateSlugMap(favouriteVendors),
    [favouriteVendors]
  );

  const removeFavourite = async (vendorId) => {
    try {
      await dispatch(removeFavouriteVendor(vendorId))
        .unwrap()
        .then(() => dispatch(fetchFavouriteVendorsItems()));
    } catch (err) {
      console.error("Failed to toggle favourite:", err);
    }
  };

  useEffect(() => {
    dispatch(fetchFavouriteVendorsItems());
    dispatch(fetchFeaturedvendorsItems());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(favouriteVendors) && Array.isArray(featuredVendors)) {
      const featuredIds = new Set(featuredVendors.map((f) => f.vendor_id._id));

      const intersection = favouriteVendors.filter((fav) =>
        featuredIds.has(fav.vendor_id?._id || fav._id)
      );

      // Favourites that are NOT featured
      const nonFeatured = favouriteVendors.filter(
        (fav) => !featuredIds.has(fav.vendor_id?._id || fav._id)
      );

      setFeaturedFavouriteVendors(intersection);
      setNonFeaturedFavouriteVendors(nonFeatured);
    }
  }, [favouriteVendors, featuredVendors]);

  // Add loading and empty states
  if (!favouriteVendors) {
    return <div>Loading...</div>;
  }

  if (favouriteVendors.length === 0) {
    return (
      <RootWrapperRewardsone>
        <Dashboard>
          <Wrapper>
            <DashboardHeader>
              <Frame1462>
                <WelcomeJames>Favourites</WelcomeJames>
              </Frame1462>
            </DashboardHeader>
            <Favouritevendorsyet style={{ padding: "2rem", textAlign: "center" ,}}>
              No favourite vendors yet
            </Favouritevendorsyet>
          </Wrapper>
        </Dashboard>
      </RootWrapperRewardsone>
    );
  }

  return (
    <RootWrapperRewardsone>
      <Dashboard>
        <Wrapper>
          <DashboardHeader>
            <Frame1462>
              <WelcomeJames>Favourites</WelcomeJames>
            </Frame1462>
          </DashboardHeader>
        </Wrapper>
      </Dashboard>

      {/* Featured Favourites Section */}
      {featuredFavouriteVendors.length > 0 && (
        <FeaturedVendorsWrapper>
          <Wrapper>
            <FeaturedVendors>Featured Vendors</FeaturedVendors>
            <FeaturedGrid>
              {featuredFavouriteVendors.map((fav) => {
                const vendor = fav.vendor_id;

                // Find slug using vendor's ID
                const slug = Object.keys(slugMap).find(
                  (key) => slugMap[key] === vendor?._id
                );

                if (!vendor || !slug) return null;

                return (
                  <TopCard key={vendor._id}>
                    <DealCardMore
                      title={vendor?.short_description}
                      reward={vendor?.reward}
                      badgeType={vendor?.vendor_badge || "Featured"}
                      logoUrl={vendor?.compay_logo}
                      onClick={() => {
                        navigate(`/vendors/${slug}`, { state: { vendor } });
                        scrollToTop();
                      }}
                      thumpnailUrl={vendor?.website_thumbnail}
                      isFavorite={true}
                      onFavorite={() => {
                        removeFavourite(vendor._id);
                      }}
                    />
                  </TopCard>
                );
              })}
            </FeaturedGrid>
          </Wrapper>
        </FeaturedVendorsWrapper>
      )}

      {/* All Favourites Section */}
      <FeaturedVendorsWrapper>
        <Wrapper>
          <FeaturedVendors>Vendors</FeaturedVendors>
          <Frame47_0001>
            {nonFeaturedFavouriteVendors.map((fav) => {
              const vendor = fav.vendor_id;

              // Find slug using vendor's ID
              const slug = Object.keys(slugMap).find(
                (key) => slugMap[key] === vendor?._id
              );

              if (!vendor || !slug) return null;

              return (
                <DealCardWrapper key={vendor._id}>
                  <DealCard
                    title={vendor?.short_description}
                    reward={vendor?.reward}
                    badgeType={vendor?.vendor_badge}
                    logoUrl={vendor?.compay_logo}
                    onClick={() => {
                      navigate(`/vendors/${slug}`, { state: { vendor } });
                      scrollToTop();
                    }}
                    isFavorite={true}
                    onFavorite={() => {
                      removeFavourite(vendor._id);
                    }}
                  />
                </DealCardWrapper>
              );
            })}
          </Frame47_0001>
        </Wrapper>
      </FeaturedVendorsWrapper>
    </RootWrapperRewardsone>
  );
}

const TopCard = styled.div`
  flex: 1;
  width: 100%;
`;

const Frame47_0001 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr); // default 4 columns
  gap: 32px;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr); // 2 columns on tablets
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr); // 1 column on mobile
  }
`;

const DealCardWrapper = styled.div`
  width: 220px;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;


const RootWrapperRewardsone = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  gap: 24px;
  padding-block: clamp(1rem, 5vw, 3.75rem);
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding-inline: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  gap: 16px;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
  }
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  flex-grow: 1;
  @media (max-width: 1024px) and (min-width: 768px) {
    flex-direction: column;
  }

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 799px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;


const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(1.5rem, 5vw, 3rem);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: left;
`;
const Favouritevendorsyet = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  justify-items: flex-start;
  width: 100%;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr); // 2 columns on tablets
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr; // 1 column on mobile
  }
`;
const FeaturedVendors = styled.div`
  color: var(--Color-Text-Text, #212543);
  align-self: stretch;
  /* H6 */
  font-family: Montserrat;
  font-size: var(--Fonts-H6, 24px);
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.24px;
`;
const FeaturedVendorsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding-inline: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;
